import { motion } from 'framer-motion'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { formatDate } from '../../../../shared/lib/format'
import { deviceColors } from './chartColors'
import type { AnalyticsData } from './types'

interface AnalyticsChartsProps {
  analyticsData: AnalyticsData
}

export default function AnalyticsCharts({
  analyticsData,
}: AnalyticsChartsProps) {
  return (
    <>
      {/* Charts Grid */}
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Clicks Over Time */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="rounded-lg bg-white p-6 shadow dark:bg-gray-800"
        >
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            Clicks Over Time
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={analyticsData.clicksOverTime}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis
                dataKey="date"
                stroke="#6B7280"
                fontSize={12}
                tickFormatter={value => formatDate(value)}
              />
              <YAxis stroke="#6B7280" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                }}
                labelFormatter={value => new Date(value).toLocaleDateString()}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="clicks"
                stackId="1"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.6}
                name="Total Clicks"
              />
              <Area
                type="monotone"
                dataKey="uniqueClicks"
                stackId="2"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.6}
                name="Unique Clicks"
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Device Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="rounded-lg bg-white p-6 shadow dark:bg-gray-800"
        >
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            Device Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analyticsData.deviceStats}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
                label={({ device, percentage }) => `${device} ${percentage}%`}
              >
                {analyticsData.deviceStats.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={deviceColors[index % deviceColors.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                }}
                formatter={(value: number) => [
                  value.toLocaleString(),
                  'Clicks',
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Country Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="rounded-lg bg-white p-6 shadow dark:bg-gray-800"
        >
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            Top Countries
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={analyticsData.countryStats}
              margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="country" stroke="#6B7280" fontSize={12} />
              <YAxis stroke="#6B7280" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                }}
                formatter={(value: number) => [
                  value.toLocaleString(),
                  'Clicks',
                ]}
              />
              <Bar dataKey="clicks" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Hourly Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="rounded-lg bg-white p-6 shadow dark:bg-gray-800"
        >
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            24-Hour Activity Pattern
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analyticsData.hourlyActivity}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis
                dataKey="hour"
                stroke="#6B7280"
                fontSize={12}
                tickFormatter={value => `${value}:00`}
              />
              <YAxis stroke="#6B7280" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                }}
                labelFormatter={value => `${value}:00`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="clicks"
                stroke="#F59E0B"
                strokeWidth={2}
                dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
                name="Clicks"
              />
              <Line
                type="monotone"
                dataKey="users"
                stroke="#06B6D4"
                strokeWidth={2}
                dot={{ fill: '#06B6D4', strokeWidth: 2, r: 4 }}
                name="Active Users"
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    </>
  )
}
